import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { errorMessage } from '../lib/errors';
import { API_URL, lastTenant, useSession } from '../lib/session';
import { ErrorBox, Field, Input } from '../components/ui';

function Brand() {
  return (
    <div className="login-brand">
      {/* Logo de l'application (commun à toutes les entreprises ; chacune retrouve le sien une fois connectée) */}
      <img src="/krotime-logo.png" alt="Krotime" className="app-wordmark" />
    </div>
  );
}

export function LoginPage() {
  const { login, loginMfa } = useSession();
  const [tenant, setTenant] = useState(lastTenant());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTenant, setShowTenant] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const mfa = await login(tenant.trim(), email, password);
      if (mfa) setMfaToken(mfa.mfaToken);
    });
  };
  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    void run(() => loginMfa(tenant.trim(), mfaToken!, code));
  };

  if (mfaToken)
    return (
      <div className="login">
        <form className="card form" onSubmit={submitCode}>
          <Brand />
          <h2>Double vérification</h2>
          <p className="muted">
            Saisissez le code à 6 chiffres affiché par votre application d’authentification (ou un de vos
            codes de secours).
          </p>
          <Field label="Code de vérification">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
            />
          </Field>
          <ErrorBox message={error} />
          <button className="btn primary block" disabled={busy || code.trim().length < 6}>
            {busy ? 'Vérification…' : 'Valider'}
          </button>
          <button
            type="button"
            className="link"
            onClick={() => (setMfaToken(null), setCode(''), setPassword(''))}
          >
            Retour
          </button>
        </form>
      </div>
    );

  return (
    <div className="login">
      <form className="card form" onSubmit={submit}>
        <Brand />
        <Field label="E-mail">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </Field>
        <Field label="Mot de passe">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>
        {showTenant ? (
          <Field label="Société (identifiant)">
            <Input value={tenant} onChange={(e) => setTenant(e.target.value)} />
          </Field>
        ) : (
          <button type="button" className="link" onClick={() => setShowTenant(true)}>
            Société : {tenant}
          </button>
        )}
        <ErrorBox message={error} />
        <button className="btn primary block" disabled={busy}>
          {busy ? 'Connexion…' : 'Se connecter'}
        </button>
        <Link
          className="link"
          to={`/mot-de-passe-oublie?societe=${encodeURIComponent(tenant)}&email=${encodeURIComponent(email)}`}
        >
          Mot de passe oublié ?
        </Link>
      </form>
    </div>
  );
}

const publicPost = async (path: string, body: unknown) => {
  const r = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const e = (await r.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(e?.error?.message ?? 'Erreur, réessayez');
  }
};

/** Demande d’un lien de réinitialisation (réponse identique que le compte existe ou non). */
export function ForgotPasswordPage() {
  const params = new URLSearchParams(window.location.search);
  const [tenant, setTenant] = useState(params.get('societe') || lastTenant());
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await publicPost('/v1/auth/password/forgot', { tenant: tenant.trim(), email });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <form className="card form" onSubmit={(e) => void submit(e)}>
        <Brand />
        <h2>Mot de passe oublié</h2>
        {sent ? (
          <p role="status">
            Si un compte correspond à <strong>{email}</strong>, un e-mail vient de lui être envoyé avec un
            lien valable 30 minutes. Pensez à regarder dans les courriers indésirables.
          </p>
        ) : (
          <>
            <p className="muted">
              Indiquez l’e-mail de votre compte : vous recevrez un lien pour choisir un nouveau mot de passe.
            </p>
            <Field label="E-mail">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </Field>
            <Field label="Société (identifiant)">
              <Input value={tenant} onChange={(e) => setTenant(e.target.value)} required />
            </Field>
            <ErrorBox message={error} />
            <button className="btn primary block" disabled={busy}>
              Recevoir le lien
            </button>
          </>
        )}
        <Link className="link" to="/">
          Retour à la connexion
        </Link>
      </form>
    </div>
  );
}

/** Lien reçu par e-mail : le jeton est dans le fragment (#…), jamais envoyé dans les journaux des serveurs web. */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token] = useState(() => window.location.hash.slice(1));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    // the secret leaves the address bar (history, screen sharing)
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) return setError('Les deux mots de passe ne correspondent pas');
    setBusy(true);
    setError(null);
    try {
      await publicPost('/v1/auth/password/reset', { token, password });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <form className="card form" onSubmit={(e) => void submit(e)}>
        <Brand />
        <h2>Nouveau mot de passe</h2>
        {done ? (
          <>
            <p role="status">Votre mot de passe est modifié. Vos autres appareils ont été déconnectés.</p>
            <button type="button" className="btn primary block" onClick={() => navigate('/')}>
              Se connecter
            </button>
          </>
        ) : !token ? (
          <p>Lien incomplet : ouvrez le lien reçu par e-mail, ou refaites une demande.</p>
        ) : (
          <>
            <p className="muted">12 caractères au minimum.</p>
            <Field label="Nouveau mot de passe">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                autoFocus
              />
            </Field>
            <Field label="Confirmer le mot de passe">
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>
            <ErrorBox message={error} />
            <button className="btn primary block" disabled={busy}>
              Enregistrer
            </button>
          </>
        )}
        {!done && (
          <Link className="link" to="/mot-de-passe-oublie">
            Refaire une demande
          </Link>
        )}
      </form>
    </div>
  );
}
